import React, { useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import i18n, { SettingsType } from 'utils/i18n';
import Link from 'next/link';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, Configure } from 'react-instantsearch';
import Image from 'next/image';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import Header from './header';
import SearchBar from './search-bar';
import RichText from '@madebyconnor/rich-text-to-jsx';

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_CLIENT_KEY,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY
);

interface HeroProps {
  title: string;
  text: any;
}

enum LanguageTypes {
  da = 'da-dk',
  de = 'de-de',
  'en-us' = 'en-us',
}

type FormData = {
  products: string[];
};

export const LandingHero: React.FC<HeroProps> = ({ title, text }) => {
  const { locale } = useRouter();
  const { solutions } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const keys = useMemo(() => solutions.map(() => uuidv4()), [solutions]);
  const { register, handleSubmit, watch, control, setValue } = useForm<FormData>({
    defaultValues: {
      products: [],
    },
  });
  const selectedProducts = watch('products', []);

  return (
    <StyledSection style={{ backgroundImage: "url('/images/hero.jpg')" }}>
      <Header />
      <StyledSearchContainer>
        <StyledTextContainer>
          <h1>{title}</h1>
          <h6>
            <RichText richText={text.json} />
          </h6>
        </StyledTextContainer>
        <InstantSearch searchClient={searchClient} indexName="continia.portal-test">
          <Configure
            hitsPerPage={7}
            optionalFilters={[`language.name:${locale}`]}
            facetFilters={[selectedProducts ? selectedProducts?.map((product) => `product.value:${product}`) : '']}
          />
          <SearchBar />
        </InstantSearch>
      </StyledSearchContainer>
      <StyledSvgContainer>
        <svg width="298" height="321" viewBox="0 0 298 321" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M298 321V0.936523C298 83.227 231.29 149.937 149 149.937V321H298Z"
            fill="#F5EDDA"
            fillOpacity="0.7"
          />
          <path
            d="M0 298.936C0 216.646 66.7096 149.936 149 149.936V320.999H0V298.936Z"
            fill="#F5EDDA"
            fillOpacity="0.7"
          />
        </svg>
      </StyledSvgContainer>
      <StyledSvgContainer>
        <svg width="299" height="256" viewBox="0 0 299 256" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M149.5 255.457H299V0.899414C299 71.1937 242.015 128.178 171.721 128.178H149.5V255.457Z"
            fill="#F5EDDA"
          />
          <path d="M0 255.457C0 185.162 56.9848 128.178 127.279 128.178H149.5V255.457H0Z" fill="#F5EDDA" />
        </svg>
      </StyledSvgContainer>
    </StyledSection>
  );
};

const StyledSection = styled.section`
  background-repeat: no-repeat;
  background-size: cover;
  height: 47.2vw;
  width: 100%;
  position: relative;
`;

const StyledSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 620px;
  margin-left: 280px;
  padding-top: 200px;
`;

const StyledTextContainer = styled.div`
  h1 {
    font-family: 'alliance_no.2bold';
    font-weight: 700;
    font-size: 85px;
    line-height: 80px;
  }

  h6 {
    font-family: 'Alliance No.2';
    font-weight: 400;
    font-size: 24px;
    line-height: 70px;
  }
`;

const StyledSvgContainer = styled.div`
  position: absolute;
  bottom: -5px;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

export default LandingHero;
